/**
 * Employee Competencies React Query Hook
 *
 * Custom hook to fetch competencies for an employee (basis + vak).
 */

import { useQuery } from '@tanstack/react-query';
import { competenciesApi, type EmployeeCompetencies } from '../../lib/competenciesApi';

// Query keys for cache management
export const employeeCompetenciesKeys = {
  all: ['employeeCompetencies'] as const,
  detail: (employeeId: number, scoringModel?: 'tasks_v1' | 'core_tasks_v2') =>
    [...employeeCompetenciesKeys.all, employeeId, scoringModel ?? 'tasks_v1'] as const,
};

/**
 * Hook to fetch competencies for an employee
 * Returns both basis (directly assigned) and vak (task-related) competencies
 *
 * `scoringModel` (Epic 4, Story 4.2, AC #3, AD-4) — optioneel; geef
 * `evaluation.scoringModel` mee vanuit de wizard zodat `core_tasks_v2`
 * de compactere set (via toegewezen kerntaken) ophaalt. Weglaten (alle
 * andere bestaande aanroepers) geeft het ongewijzigde `tasks_v1`-gedrag.
 */
export function useEmployeeCompetencies(employeeId: number, scoringModel?: 'tasks_v1' | 'core_tasks_v2') {
  return useQuery<EmployeeCompetencies>({
    queryKey: employeeCompetenciesKeys.detail(employeeId, scoringModel),
    queryFn: () => competenciesApi.getForEmployee(employeeId, scoringModel),
    enabled: !!employeeId && employeeId > 0,
  });
}
